import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {Subscription} from 'rxjs';
import {SortTable, SortTableRow} from '../../../../../shared/models/sort-table/types';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {SearchService} from '../../../../../core/services/search.service';
import {DevicesService} from '../../../../../core/services/devices.service';
import {Device, DeviceId, DeviceType} from '../../../../../shared/models/device';
import {FeatureFlagsService, FeatureFlag} from '../../../../../core/services/feature-flags.service';
import {
  SortTableCellButtons,
  SortTableCellKebabMenu,
  SortTableCellTextPill,
  SortTableCellTitleSharingInfo,
} from '../../../../../shared/models/sort-table/cell-classes';
import {ButtonConfig} from '../../../../../shared/models/button';
import {Option} from '../../../../../shared/models/option';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {DeviceDeletedAction} from '../../../../../core/actions/devices.actions';
import {HubId} from '../../../../../shared/models/hub';
import {SessionService} from '../../../../../core/services/session.service';
import {BaseTableViewComponent} from '../../../../components/_base-table-view/base-table-view.component';
import {debounceTime} from 'rxjs/operators';
import {URL_PARAM_KEY_FILTERS, UrlService} from '../../../../../core/services/url.service';
import {Filters} from '../../../../../shared/models/filter';
import {TitleChangedAction} from '../../../../../core/actions/title.actions';
import {SubtitleItem} from '../../../../../core/state/title.state';
import {ObjectModel, ObjectType} from '../../../../../shared/models/object.model';
import {SpinnerComponent} from '../../../../components/spinner/spinner.component';
import {SortTableComponent} from '../../../../components/sort-table/sort-table.component';
import {DeviceEditorComponent} from '../../../../components/device-editor/device-editor.component';
import {BacnetPointDiscoveryComponent} from '../../../../components/bacnet-point-discovery/bacnet-point-discovery.component';
import {ModbusPointEditorComponent} from '../../../../components/modbus-point-editor/modbus-point-editor.component';
import {LoRaWANPointsComponent} from '../../../../components/lorawan-points/lorawan-points.component';
import {HubEditorComponent} from '../../../../components/hub-editor/hub-editor.component';
import {DialogBoxButtonColor} from 'src/shared/models/dialog-box';
import {PermissionsService} from '../../../../../core/services/permissions.service';
import {Permission} from '../../../../../shared/models/fga/permission';
import {EMPTY_ID} from '../../../../../shared/constants/empty-id';
import {DeviceMenuOptionId} from '../../../../../shared/constants/menu-option-ids';
import {MotiChatService} from '../../../../../moti/services/moti-chat.service';

const COL_IDX_KEBAB = 0;
const COL_IDX_NAME = 1;
const COL_IDX_BUTTONS = 2;
const COL_IDX_ID = 3;
const COL_IDX_PROTOCOL = 4;
const COL_IDX_PROTOCOL_ADDR = 5;
const COL_IDX_NET_ADDR = 6;
const COL_IDX_HUB = 7;

@Component({
  selector: 'motn-devices-existing',
  templateUrl: './devices-existing.component.html',
  styleUrls: ['./devices-existing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    SortTableComponent,
    DeviceEditorComponent,
    BacnetPointDiscoveryComponent,
    ModbusPointEditorComponent,
    LoRaWANPointsComponent,
    HubEditorComponent,
  ],
})
export class DevicesExistingComponent extends BaseTableViewComponent implements OnInit, OnDestroy {
  devices: Device[];
  searchTerm: string = '';
  filters: Filters;
  editingDevice: DeviceId = EMPTY_ID;
  editingHub: HubId = EMPTY_ID;

  private devicesSub: Subscription;
  private searchSub: Subscription;
  private filtersSub: Subscription;

  bacnetDiscoveryDevice: DeviceId = EMPTY_ID;
  modbusConfigureDevice: DeviceId = EMPTY_ID;
  lorawanPointsDevice: DeviceId = EMPTY_ID;


  private canManage: { [deviceId: DeviceId]: boolean } = {};


  constructor(
    private store: Store<AppState>,
    private shadeTools: ShadeToolsService,
    private devicesService: DevicesService,
    private searchService: SearchService,
    private urlService: UrlService,
    private session: SessionService,
    private permissionsService: PermissionsService,
    private motiChatService: MotiChatService,
    private featureFlagsService: FeatureFlagsService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.searchService.enableSearch();
    this.searchService.enableFilters({
      by: {
        devices: false,
        hubs: true,
        owners: true,
        metadata: false,
      },
    });

    this.searchSub = this.urlService.searchTerm$.pipe(
      debounceTime(150),
    ).subscribe((term) => {
      this.searchTerm = term;
      // Only update table if we have devices loaded
      if (this.devices && this.devices.length > 0) {
        this.updateTable();
      }
    });

    this.filtersSub = this.urlService.filters$.subscribe({
      next: (filters) => {
        this.filters = filters;
        this.updateTable();
      },
    });

    this.devicesSub = this.store.select('devices').subscribe(
      (state) => {
        for (let device of state.devices) {
          const row = this.tableRows[device.ID];
          if (row) {
            this.upsertRowData(row, device);
          }
        }

        this.devices = state.devices;
      },
    );
    this.devicesService.fetchUserDevices$().subscribe(
      (devices) => {
        this.devices = devices;
        this.loading = false;
        this.updateTable();
      },
    );
  }


  ngOnDestroy() {
    this.devicesSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.filtersSub?.unsubscribe();
  }


  protected updateTitle() {
    const subtitle: SubtitleItem[] = [];

    if (this.searchTerm) {
      subtitle.push(
        {
          icon: `magnifying-glass`,
          text: `Search term entered`,
        },
      );
    }

    if (this.searchTerm) {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} results`,
        },
      );
    } else {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} total`,
        },
      );
    }

    this.store.dispatch(new TitleChangedAction(
      '',
      'Devices',
      subtitle,
    ));
  }


  private deleteDevice(deviceId: DeviceId) {
    this.devicesService.deleteDevice$(deviceId).subscribe(
      () => {
        this.store.dispatch(new DeviceDeletedAction(deviceId));
        delete this.tableRows[deviceId];
        this.updateTable();
        this.shadeTools.closeAll();
      },
      (err) => {
        this.shadeTools.errorDialog(err);
      },
    );
  }


  protected updateTable() {
    if (!this.devices || this.devices.length === 0) {
      return;
    }

    const table: SortTable = {
      css: {
        'min-width': '1030px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': '40px',
        },
      };

      table.columns[COL_IDX_NAME] = {
        name: 'Device Name',
        css: {
          'width': 'calc(100% - 890px)',
        },
        sortingFn: SortTableCellTitleSharingInfo.sortingFnTitle,
      };

      table.columns[COL_IDX_BUTTONS] = {
        name: '',
        css: {
          'width': '150px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_ID] = {
        name: 'ID',
        css: {
          'width': '100px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnPillText,
        sortDefault: 'down',
      };

      table.columns[COL_IDX_NET_ADDR] = {
        name: 'Network Address',
        infoDialogText: NETWORK_ADDR_INFO,
        css: {
          'width': '200px',
          'justify-content': 'left',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_PROTOCOL_ADDR] = {
        name: 'Protocol Address',
        css: {
          'width': '150px',
          'justify-content': 'left',
          'text-align': 'left',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_PROTOCOL] = {
        name: 'Protocol',
        css: {
          'width': '100px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_HUB] = {
        name: 'Motion Hub',
        css: {
          'width': '150px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnPillText,
      };
    }

    let devices: Device[] = this.devices;

    if (this.searchTerm) {
      devices = this.searchService.searchDevices(this.searchTerm, devices);
    }

    if (this.filters) {
      devices = this.searchService.filterDevices(this.filters, devices);
    }

    for (let device of devices) {
      let row: SortTableRow = this.tableRows[device.ID];

      if (!row) {
        row = this.tableRows[device.ID] = {
          context: '' + device.ID,
          cells: [],
        };
      }

      this.upsertRowData(row, device);
      table.rows.push(row);
    }

    this.searchService.setResultQty(table.rows.length);
    this.table$.next(table);
    this.updateTitle();
  }


  /**
   * For inserting data into a brand new table row or updating an existing
   * table row.
   *
   * @param row
   * @param device
   * @private
   */
  protected upsertRowData(row: SortTableRow, device: Device) {
    if (!this.canManage[device.ID]) {
      this.canManage[device.ID] = device.Owner === this.session.userId;
    }

    const buttons: ButtonConfig[] = [];

    let protocolText: string = '';

    switch (device.Type) {
      case 'bacnet':
        protocolText = 'BACnet/IP';

        buttons.push({
          id: 'discovery',
          text: 'Discover Points',
          size: 'small',
          colors: ['white', 'indigo'],
          callback: () => {
            this.bacnetDiscoveryDevice = device.ID;
          },
        });

        break;
      case 'modbus':
        protocolText = 'Modbus TCP';

        buttons.push({
          id: 'point-config',
          text: 'Configure Points',
          size: 'small',
          colors: ['white', 'indigo'],
          callback: () => {
            this.modbusConfigureDevice = device.ID;
          },
        });

        break;
      case 'lorawan':
        protocolText = 'LoRaWAN';

        if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_VIEW_POINTS)) {
          buttons.push({
            id: 'view-points',
            text: 'Configure Points',
            size: 'small',
            colors: ['white', 'indigo'],
            callback: () => {
              this.lorawanPointsDevice = device.ID;
            },
          });
        }

        break;
    }

    row.cells[COL_IDX_NAME] = new SortTableCellTitleSharingInfo({
      title: device.Name,
      object: new ObjectModel({
        id: device.ID,
        type: ObjectType.DEVICE,
        name: device.Name,
        owner: device.Owner,
      }),
    });
    row.cells[COL_IDX_BUTTONS] = new SortTableCellButtons(buttons);
    row.cells[COL_IDX_ID] = new SortTableCellTextPill('', '' + device.ID).setPillLetter('d');
    // Hide Network Address column for LoRaWAN devices
    row.cells[COL_IDX_NET_ADDR] = device.Type === DeviceType.LoRaWAN
      ? new SortTableCellTextPill('', '', true)
      : new SortTableCellTextPill(device.IP, '', true);
    row.cells[COL_IDX_PROTOCOL_ADDR] = new SortTableCellTextPill(device.Address, '', true);
    row.cells[COL_IDX_PROTOCOL] = new SortTableCellTextPill(protocolText, '');
    row.cells[COL_IDX_HUB] = new SortTableCellTextPill('', '' + device.Hub)
      .setPillLetter('h')
      .addClickCallback(() => this.onHubPillClick(device.Hub));

    // Max 6 options: Configure, Delete, View Tiles, Discover Points, Reset Timeout, Chat
    const kebabCell = new SortTableCellKebabMenu(device.ID, [], device.Name, false, 6);

    // Add click callback that will be called when kebab menu is opened
    kebabCell.addClickCallback(() => {
      this.loadKebabOptions(kebabCell, device);
    });

    row.cells[COL_IDX_KEBAB] = kebabCell;
  }

  private getViewTilesOption(device: Device): Option {
    const filters: Filters = {
      devices: [device.ID],
    };
    const viewTilesParams = {};
    viewTilesParams[URL_PARAM_KEY_FILTERS] = JSON.stringify(filters);

    return {
      id: DeviceMenuOptionId.VIEW_TILES,
      name: 'View Associated Tiles',
      href: UrlService.controlsUrls.tilesExisting,
      params: viewTilesParams,
    };
  }

  /**
   * Load kebab menu options by checking permissions
   */
  private loadKebabOptions(kebabCell: SortTableCellKebabMenu, device: Device): void {
    if (kebabCell.isLoading) return;

    kebabCell.isLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: device.ID,
      ObjectType: ObjectType.DEVICE,
      Permissions: [Permission.CAN_CONFIGURE, Permission.CAN_DELETE, Permission.CAN_CHAT]
    }).subscribe({
      next: (response) => {
        const kebabOptions: Option[] = [];

        if (response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed) {
          kebabOptions.push({
            id: DeviceMenuOptionId.CONFIGURE,
            name: 'Configure Device',
            callback: () => this.editingDevice = device.ID,
          });
        }

        if (response.Results.find(r => r.Permission === Permission.CAN_DELETE)?.Allowed) {
          kebabOptions.push({
            id: DeviceMenuOptionId.DELETE,
            name: 'Delete Device',
            callback: () => this.showDeleteDeviceDialog(device),
          });
        }

        kebabOptions.push(this.getViewTilesOption(device));

        // Add device-specific options based on device type
        if (device.Type === DeviceType.BACnet) {
          kebabOptions.push(
            {
              id: DeviceMenuOptionId.DISCOVER_POINTS,
              name: 'Discover BACnet Points',
              callback: () => {
                this.bacnetDiscoveryDevice = device.ID;
              },
            },
            this.getResetTimeoutOption(device),
          );
        } else if (device.Type === DeviceType.Modbus) {
          kebabOptions.push(
            {
              id: DeviceMenuOptionId.CONFIGURE_MODBUS_POINTS,
              name: 'Configure Modbus Points',
              callback: () => {
                this.modbusConfigureDevice = device.ID;
              },
            },
            this.getResetTimeoutOption(device),
          );
        } else if (device.Type === DeviceType.LoRaWAN) {
          if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_VIEW_POINTS)) {
            kebabOptions.push({
              id: DeviceMenuOptionId.DISCOVER_POINTS,
              name: 'Configure LoRaWAN Points',
              callback: () => {
                this.lorawanPointsDevice = device.ID;
              },
            });
          }
        }

        // Chat option
        if (response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed) {
          kebabOptions.push({
            id: DeviceMenuOptionId.CHAT,
            name: 'Chat',
            icon: 'fa-kit fa-chat',
            callback: () => {
              this.motiChatService.openChat(device.ID, device.Name, ObjectType.DEVICE);
            },
          });
        }

        kebabCell.options = kebabOptions;
        kebabCell.isLoading = false;
      },
      error: (error) => {
        console.error('Error checking device permissions:', error);
        kebabCell.isLoading = false;
      },
    });
  }

  private getResetTimeoutOption(device: Device): Option {
    return {
      id: DeviceMenuOptionId.RESET_TIMEOUT,
      name: 'Reset Timeout',
      callback: () => {
        this.shadeTools.startDialog(
          'Reset Timeout',
          `A timeout is when a Motion Hub intentionally stops reading from a device after having trouble communicating with it. Timeouts last up to 10 minutes. If a tile appears to not be successfully updating with latest data, the device upon which the tile depends may be in the middle of a timeout.\n\nTo reset the timeout for this device, click "Reset" below.`,
          [
            {
              text: 'Reset',
              callback: () => {
                this.devicesService.resetTimeout$(device.ID).subscribe(
                  () => {
                    this.shadeTools.simpleOkDialog('Success', 'All timeouts for this device have been removed.');
                  },
                  () => {
                    this.shadeTools.simpleOkDialog('Error', `An error occurred while attempting to remove timeouts for the device named ${device.Name}.`);
                  },
                );
              },
              color: DialogBoxButtonColor.Red,
            },
            {
              text: 'Cancel',
              callback: () => {
              },
              color: DialogBoxButtonColor.Alt,
            },
          ],
        );
      },
    };
  }

  private showDeleteDeviceDialog(device: Device): void {
    this.shadeTools.startDialog(
      `Delete Device ${device.ID}`,
      `Are you sure you want to delete the device named ${device.Name}?`,
      [
        {
          text: 'Yes, Delete Device',
          callback: () => {
            this.deleteDevice(device.ID);
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'No, Go Back',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      false,
      false,
      false,
      false,
    );
  }

  /**
   * Handle hub pill click - check permission before opening hub configuration drawer
   */
  private onHubPillClick(hubId: HubId): void {
    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: hubId,
      ObjectType: ObjectType.HUB,
      Permissions: [Permission.CAN_CONFIGURE]
    }).subscribe({
      next: (response) => {
        const canConfigure = response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed;

        if (canConfigure) {
          this.editingHub = hubId;
        } else {
          this.shadeTools.simpleOkDialog(
            'Permission Denied',
            'You do not have permission to configure this hub.'
          );
        }
      },
      error: (error) => {
        console.error('Error checking hub permissions:', error);
        this.shadeTools.errorDialog(error);
      },
    });
  }
}

const NETWORK_ADDR_INFO = `Depending on the protocol, this may be simply a local IP address or it could be a more complex value.

A common example of a more complex value is a BACnet router linking an MS/TP network to an IP network. These network addresses can take on different forms, such as:
 • 4:10@10.0.1.201
 • 100:0x03F2@192.168.50.100`;
